import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, typography, spacing } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { withAlpha } from '../../theme';

export interface QuickReplySetCardProps {
  quickReplySet: {
    _id?: string;
    name: string;
    description?: string;
    items?: any[];
    itemCount?: number; // Number of items in this set (from backend)
    isActive?: boolean;
    isDefault?: boolean;
    metadata?: {
      usageCount?: number;
    };
  };
  onPress?: (setId: string) => void;
  onEdit?: (setId: string) => void;
  onDelete?: (setId: string) => void;
  onManageItems?: (setId: string) => void;
  onSend?: (setId: string) => void;
  showActions?: boolean;
  containerStyle?: any;
}

export const QuickReplySetCard: React.FC<QuickReplySetCardProps> = ({
  quickReplySet,
  onPress,
  onEdit,
  onDelete,
  onManageItems,
  onSend,
  showActions = true,
  containerStyle,
}) => {
  const styles = useThemedStyles(getStyles);
  const handlePress = () => {
    if (quickReplySet._id && onPress) {
      onPress(quickReplySet._id);
    }
  };

  const handleEdit = (e: any) => {
    e.stopPropagation();
    if (quickReplySet._id && onEdit) {
      onEdit(quickReplySet._id);
    }
  };

  const handleDelete = (e: any) => {
    e.stopPropagation();
    if (quickReplySet._id && onDelete) {
      onDelete(quickReplySet._id);
    }
  };

  const handleManageItems = (e: any) => {
    e.stopPropagation();
    if (quickReplySet._id && onManageItems) {
      onManageItems(quickReplySet._id);
    }
  };

  const handleSend = (e: any) => {
    e.stopPropagation();
    if (quickReplySet._id && onSend) {
      onSend(quickReplySet._id);
    }
  };

  return (
    <TouchableOpacity
      style={[styles.container, containerStyle]}
      onPress={handlePress}
      activeOpacity={0.7}
    >
      <View style={styles.content}>
        <View style={styles.header}>
          <View style={styles.iconContainer}>
            <Ionicons name="chatbubble-ellipses-outline" size={24} color={colors.primary} />
          </View>
          <View style={styles.infoContainer}>
            <View style={styles.titleRow}>
              <Text style={styles.name} numberOfLines={1}>
                {quickReplySet.name}
              </Text>
              {quickReplySet.isDefault && (
                <View style={styles.defaultBadge}>
                  <Text style={styles.defaultBadgeText}>Default</Text>
                </View>
              )}
              {quickReplySet.isActive === false && (
                <View style={styles.inactiveBadge}>
                  <Text style={styles.inactiveBadgeText}>Inactive</Text>
                </View>
              )}
            </View>
            {quickReplySet.description && (
              <Text style={styles.description} numberOfLines={2}>
                {quickReplySet.description}
              </Text>
            )}
          </View>
        </View>

        <View style={styles.actionsRow}>
          <View style={styles.statsRow}>
            <View style={styles.statItem}>
              <Ionicons name="list" size={14} color={colors.textLight} style={styles.statIcon} />
              <Text style={styles.statText}>
                {quickReplySet.itemCount !== undefined ? quickReplySet.itemCount : (quickReplySet.items?.length || 0)} items
              </Text>
            </View>
            {quickReplySet.metadata?.usageCount !== undefined && (
              <View style={styles.statItem}>
                <Ionicons name="stats-chart" size={14} color={colors.textLight} style={styles.statIcon} />
                <Text style={styles.statText}>
                  {quickReplySet.metadata.usageCount} uses
                </Text>
              </View>
            )}
          </View>

          {showActions && (
            <View style={styles.actionsContainer}>
              <TouchableOpacity
                style={styles.actionButton}
                onPress={handleSend}
                activeOpacity={0.7}
              >
                <Ionicons name="send-outline" size={18} color="#25D366" />
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.actionButton}
                onPress={handleManageItems}
                activeOpacity={0.7}
              >
                <Ionicons name="list-outline" size={18} color={colors.primary} />
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.actionButton}
                onPress={handleEdit}
                activeOpacity={0.7}
              >
                <Ionicons name="create-outline" size={18} color={colors.primary} />
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.actionButton}
                onPress={handleDelete}
                activeOpacity={0.7}
              >
                <Ionicons name="trash-outline" size={18} color={colors.error} />
              </TouchableOpacity>
            </View>
          )}
        </View>
      </View>
    </TouchableOpacity>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    overflow: 'hidden',
    marginBottom: spacing.md,
    ...designTokens.shadows.sm,
  },
  content: {
    padding: spacing.md,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: spacing.sm,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.primaryTransparent,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.sm,
    flexShrink: 0,
  },
  infoContainer: {
    flex: 1,
    minWidth: 0,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.xs / 2,
    gap: spacing.xs,
    flexWrap: 'wrap',
  },
  name: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
    flex: 1,
  },
  defaultBadge: {
    backgroundColor: withAlpha(colors.success, 0.13),
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
    borderRadius: designTokens.borderRadius.sm,
  },
  defaultBadgeText: {
    fontSize: typography.fontSize.xs,
    color: colors.success,
    fontFamily: typography.fontFamily.medium,
  },
  inactiveBadge: {
    backgroundColor: withAlpha(colors.warning, 0.13),
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
    borderRadius: designTokens.borderRadius.sm,
  },
  inactiveBadgeText: {
    fontSize: typography.fontSize.xs,
    color: colors.warning,
    fontFamily: typography.fontFamily.medium,
  },
  description: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
    lineHeight: 18,
  },
  actionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: spacing.sm,
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    gap: spacing.sm,
  },
  statsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    flex: 1,
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs / 2,
    backgroundColor: colors.lightGray,
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
    borderRadius: designTokens.borderRadius.sm,
  },
  statIcon: {
    marginRight: 0,
  },
  statText: {
    fontSize: typography.fontSize.xs,
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
    fontWeight: '600',
    marginLeft: 0,
  },
  actionsContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: spacing.xs,
    flexShrink: 0,
  },
  actionButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: colors.lightGray,
    justifyContent: 'center',
    alignItems: 'center',
  },
});


